/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Callout, Tldr, FaqBlock, InlineCTA } from "./shared";

export const GeoVsSeoBody = ({ post, affiliateUrl, onTrack }) => (
  <>
    <p className="lead text-lg leading-relaxed text-white/80">
      "Is SEO dead?" No. But it's no longer enough. In 2026, the brands winning organic growth
      are running <strong>SEO</strong> and <strong>GEO (Generative Engine Optimization)</strong>{" "}
      in tandem — because the click economy and the citation economy now feed each other.
    </p>

    <Tldr>{post.tldr}</Tldr>

    <h2 id="define-each">First, define each one</h2>
    <h3>What is SEO?</h3>
    <p>
      SEO is the practice of getting a specific URL to rank on a search results page (mostly
      Google) for a specific query. The job is page-level: title, meta, backlinks, on-page
      relevance, internal links.
    </p>
    <h3>What is GEO?</h3>
    <p>
      GEO is the practice of making your brand recognizable to <em>generative</em> AI engines
      (ChatGPT, Perplexity, Claude, Google AI Overviews) so they cite you in synthesized
      answers. The job is brand- and entity-level: schema markup, sameAs links, consistent NAP,
      unlinked brand mentions.
    </p>

    <h2 id="side-by-side">Side-by-side comparison</h2>
    <div className="my-6 overflow-hidden rounded-2xl border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-white/45">
          <tr>
            <th className="p-3">Dimension</th>
            <th className="p-3">SEO</th>
            <th className="p-3">GEO</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {[
            ["Target surface", "Google / Bing results page", "ChatGPT, Perplexity, Claude, AI Overviews"],
            ["Unit of work", "A single URL", "Your whole brand entity"],
            ["Primary signal", "Backlinks + content quality", "Schema + sameAs + brand mentions"],
            ["Reward", "Click", "Citation (named in an answer)"],
            ["Measurement", "Rank, CTR, sessions", "Citation share-of-voice across engines"],
            ["Time horizon", "3–9 months", "30–90 days once SEO base exists"],
            ["Key tool", "Search Console", "Profound / Otterly / manual prompts"],
          ].map((r) => (
            <tr key={r[0]}>
              {r.map((c, i) => (
                <td key={i} className={`p-3 ${i === 0 ? "font-display font-bold text-white" : "text-white/80"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <h2 id="overlap">Where SEO and GEO overlap</h2>
    <p>Roughly 70% of the underlying work is shared. Both disciplines need:</p>
    <ul>
      <li>Clean, crawlable HTML</li>
      <li>Strong title and meta descriptions</li>
      <li>Valid JSON-LD schema</li>
      <li>An XML sitemap</li>
      <li>Authoritative inbound links and mentions</li>
      <li>Fresh, recently-updated content</li>
      <li>Page speed and mobile-friendliness</li>
    </ul>
    <p>This is why GEO doesn't replace SEO — it compounds on top of it.</p>

    <h2 id="differences">Where GEO is uniquely different</h2>
    <ol>
      <li><strong>Citations &gt; clicks.</strong> A GEO win is being named, not necessarily linked.</li>
      <li><strong>Schema is the language.</strong> AI engines parse JSON-LD as the canonical "what is this?" signal.</li>
      <li><strong>Entity authority over page authority.</strong> AI engines reason about <em>brands</em>, not URLs.</li>
      <li><strong>Unlinked mentions count.</strong> A Reddit thread naming your brand is now a real ranking signal.</li>
      <li><strong>llms.txt matters.</strong> A purpose-built AI sitemap that few sites have yet.</li>
    </ol>

    <Callout kind="info">
      <strong>Reality check:</strong> If your SEO is broken (no sitemap, blocked bots, thin
      content), GEO can't fix it. Get the SEO base right, then GEO compounds inside 60 days.
    </Callout>

    <h2 id="why-both">Why you need both in 2026</h2>
    <p>Generative answers and classic search now feed each other:</p>
    <ul>
      <li>AI Overviews drive <strong>fewer but higher-intent</strong> clicks to ranking pages.</li>
      <li>Being cited in a ChatGPT answer creates brand awareness that drives <strong>direct branded searches</strong> on Google.</li>
      <li>Branded searches improve your SEO authority — which improves your AI citation rate. The flywheel is real.</li>
    </ul>

    <h2 id="action-plan">A 30-day GEO action plan (with SEO baseline)</h2>
    {post.howto.steps.map((s, i) => (
      <div key={i} className="my-4">
        <h3 className="!mb-1">Day {i * 5 + 1}–{i * 5 + 5} — {s.name}</h3>
        <p className="!mt-1">{s.text}</p>
      </div>
    ))}

    <InlineCTA
      post={post}
      location="midpost"
      affiliateUrl={affiliateUrl}
      onTrack={onTrack}
      label={{
        title: "Want SEO + AEO + GEO done for you, on autopilot?",
        sub: "Soro researches, writes, schemas and publishes daily. Set up in 60 seconds.",
        cta: "Try Soro free",
      }}
    />

    <h2 id="measurement">How to measure GEO (without obsessing)</h2>
    <p>You can't yet check "GEO Search Console". Until that exists, run this lightweight monthly audit:</p>
    <ol>
      <li>Pick 10 buyer-intent prompts in your category ("best AI SEO tool for solo founders", etc).</li>
      <li>Ask each prompt to ChatGPT, Perplexity and Google AI Overviews.</li>
      <li>Log: were you named? Were you linked? Who else was named?</li>
      <li>Track the trend monthly. Citations should grow from 0–2 → 5+ within 90 days of proper GEO work.</li>
    </ol>

    <FaqBlock faq={post.faq} />

    <h2 id="bottom-line">The bottom line</h2>
    <p>
      SEO drives clicks. GEO drives citations. Citations are the new backlinks. In 2026 you
      need both, and they share the same foundation. Start with clean SEO basics, layer on
      Organization &amp; Person schema, allow AI bots, publish question-style answer blocks, and
      track your citation share monthly. That's it.
    </p>
    <p>Want to go deeper? Read <a href="/blog/how-to-rank-on-ai-search-engines">How to rank on AI search engines</a>.</p>
  </>
);
